import { Fragment } from 'react';

import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { useOnboardingStepSubmit, useStepSubmitHandler } from 'src/context/onboarding-step-submit';
import {
  RepresentativesContainer,
  RepresentativesPlaceholder,
  withApiSubmitActions,
} from 'src/containers/Escrow/Representatives';

import { BeneficiaryOrganization } from '.';

import { BeneficiaryContextProvider, useBeneficiaryContext } from '../model';

const Representatives = withApiSubmitActions(RepresentativesContainer, () => {
  const { beneficiary, escrowId, agreementType } = useBeneficiaryContext();
  const { clearStepError } = useOnboardingStepSubmit();

  const clearErrorOnRepresentative = ({ signatory }: { signatory?: boolean | null }) => {
    if (agreementType === AgreementFormEnum.Bipartite || signatory) clearStepError();
  };

  return {
    role: EscrowPartyTypeEnum.Beneficiary,
    escrowId,
    escrowPartyId: beneficiary!.id,
    contacts: beneficiary!.contacts,
    canUpdate: beneficiary!.policy.update,
    agreementType,
    onRepresentativeSaved: clearErrorOnRepresentative,
  };
});

const RequireRepresentatives = () => {
  const { beneficiary, agreementType } = useBeneficiaryContext();

  const requireRepresentatives = async () => {
    if (!beneficiary!.policy.update) return true;

    if (!beneficiary!.contacts.length) return 'Add at least one representative to continue.';

    if (agreementType === AgreementFormEnum.Tripartite && !beneficiary!.contacts.some(({ signatory }) => signatory))
      return 'At least one representative must be designated as a signatory.';

    return true;
  };

  useStepSubmitHandler(requireRepresentatives);

  return null;
};

const BeneficiaryDetails = ({ beneficiaryId }: { beneficiaryId: string }) => {
  const { beneficiary, mode } = useBeneficiaryContext();

  return (
    <Fragment>
      <BeneficiaryOrganization />
      {beneficiaryId ? <Representatives /> : <RepresentativesPlaceholder role={EscrowPartyTypeEnum.Beneficiary} />}
      {beneficiary && mode === 'view' && <RequireRepresentatives />}
    </Fragment>
  );
};

type Props = {
  beneficiaryId: string;
  escrowId: string;
  agreementType: AgreementFormEnum;
  canCreateBeneficiary: boolean;
  canChangeBeneficiary: boolean;
};

const EscrowBeneficiaryCore = ({
  beneficiaryId,
  escrowId,
  agreementType,
  canCreateBeneficiary,
  canChangeBeneficiary,
}: Props) => {
  return (
    <BeneficiaryContextProvider
      beneficiaryId={beneficiaryId}
      escrowId={escrowId}
      agreementType={agreementType}
      canCreateBeneficiary={canCreateBeneficiary}
      canChangeBeneficiary={canChangeBeneficiary}
    >
      <BeneficiaryDetails beneficiaryId={beneficiaryId} />
    </BeneficiaryContextProvider>
  );
};

export default EscrowBeneficiaryCore;
